import React, { useState, useEffect, useRef, useMemo } from "react";
import PanelHeader from "components/panelheader/panelHeader";
import { Card, CardBody, Row, Col } from "reactstrap";
import { getTorneos, getTorneo, guardarPartido } from "services/torneosService";
import { mapToViewModel, resultadoAlServidor } from "components/torneos/torneoMapper";
import { toastBigError } from "components/utils/toasts";
import PickScreen from "./PickScreen";
import ResultsScreen from "./ResultsScreen";
import { usePageLoading } from "hooks/usePageLoading";
import { etiquetaFecha, numerosDeFecha, ordenarFechasDesc } from "components/torneos/fechas";
import { mezclarTorneo } from "./mezclaRemota";

export default function Resultados(props) {
  const doneLoading = usePageLoading(props);
  const [loading, setLoading] = useState(true);
  const [torneos, setTorneos] = useState([]); // registros crudos (backend)
  const [torneo, setTorneo] = useState(null); // torneo seleccionado (modelo front)
  const [nav, setNav] = useState([{ level: "years" }]);
  const [saveState, setSaveState] = useState("idle");
  // Cola de resultados cargados acá que el servidor todavía no confirmó:
  // _id del partido -> { ruedaId, zonaId, fechaId, partido }. Es lo que falta
  // guardar y, a la vez, lo que la mezcla respeta cuando llega el torneo fresco
  // (ver mezclaRemota.js). `pendientesVersion` es lo que dispara el debounce:
  // el guardado ya no depende de que cambie `torneo`.
  const pendientesRef = useRef(new Map());
  const [pendientesVersion, setPendientesVersion] = useState(0);
  const guardandoRef = useRef(false);

  const current = nav[nav.length - 1];

  const years = useMemo(
    () => [...new Set(torneos.map((t) => t.ano).filter((y) => y != null))].sort((a, b) => b - a),
    [torneos]
  );
  const torneosForYear = (ano) =>
    torneos.filter((t) => t.ano === ano).sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));

  const loadTorneoInto = async (id) => {
    const rec = await getTorneo(id);
    if (rec && rec._id) {
      pendientesRef.current = new Map(); // la cola era del torneo anterior
      setTorneo(mapToViewModel(rec));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const list = await getTorneos();
        const arr = Array.isArray(list) ? list : [];
        setTorneos(arr);
        const ys = [...new Set(arr.map((t) => t.ano).filter((y) => y != null))].sort((a, b) => b - a);
        if (ys.length === 1) {
          const ano = ys[0];
          const forYear = arr
            .filter((t) => t.ano === ano)
            .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
          if (forYear.length === 1) {
            await loadTorneoInto(forYear[0]._id);
            setNav([{ level: "ruedas", ano, torneoId: forYear[0]._id }]);
          } else {
            setNav([{ level: "torneos", ano }]);
          }
        }
      } catch (error) {
        console.error("Error cargando torneos:", error);
      } finally {
        setLoading(false);
        doneLoading();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Back del navegador/celular hace pop.
  useEffect(() => {
    const onPop = () => setNav((s) => (s.length > 1 ? s.slice(0, -1) : s));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const push = (frame) => {
    setNav((s) => [...s, frame]);
    window.history.pushState({}, "");
  };
  const goBack = () => window.history.back();

  // ---- Selectores derivados ----
  const round = torneo && torneo.ruedas.find((r) => r.id === current.roundId);
  const zone = round && round.zones.find((z) => z.id === current.zoneId);
  const date = zone && zone.matchDates.find((d) => d.id === current.dateId);

  // Guarda de entidad huérfana.
  useEffect(() => {
    if (!torneo) return;
    const orphan =
      (current.roundId && !round) || (current.zoneId && !zone) || (current.dateId && !date);
    if (orphan) setNav((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }, [current, torneo, round, zone, date]);

  // Deja el resultado en la cola de guardado, direccionado con los _id de Mongo, que
  // es como lo nombra el backend. Devuelve false si el partido no tiene _id todavía:
  // eso pasa sólo con un torneo cuya estructura nunca se guardó desde el ABM, y en
  // ese caso no se toca el estado, para no mostrar en pantalla un resultado que no
  // se va a poder persistir.
  const encolarResultado = (roundId, zoneId, dateId, partido) => {
    const t = torneoRef.current;
    const rueda = t && t.ruedas.find((r) => r.id === roundId);
    const zona = rueda && rueda.zones.find((z) => z.id === zoneId);
    const fecha = zona && zona.matchDates.find((d) => d.id === dateId);
    if (!fecha || !rueda._id || !zona._id || !fecha._id || !partido._id) return false;
    pendientesRef.current.set(partido._id, {
      ruedaId: rueda._id,
      zonaId: zona._id,
      fechaId: fecha._id,
      partido: partido,
    });
    setPendientesVersion((v) => v + 1);
    return true;
  };

  const updateMatch = (roundId, zoneId, dateId, updated) => {
    if (!encolarResultado(roundId, zoneId, dateId, updated)) {
      toastBigError("No se pudo guardar", "Ese partido todavía no está guardado en el torneo.");
      return;
    }
    setTorneo((p) => ({
      ...p,
      ruedas: p.ruedas.map((r) =>
        r.id !== roundId
          ? r
          : {
              ...r,
              zones: r.zones.map((z) =>
                z.id !== zoneId
                  ? z
                  : {
                      ...z,
                      matchDates: z.matchDates.map((d) =>
                        d.id !== dateId
                          ? d
                          : { ...d, matches: d.matches.map((m) => (m.id === updated.id ? updated : m)) }
                      ),
                    }
              ),
            }
      ),
    }));
  };

  // Guardado (debounce) de los resultados cargados.
  const ESPERA_GUARDADO = 700;
  // El torneo tal como está ahora, para leerlo desde los handlers y desde el aviso
  // al cerrar, que se registran una sola vez.
  const torneoRef = useRef(torneo);
  torneoRef.current = torneo;
  // Hay un resultado que todavía no llegó al servidor: el debounce corriendo, un
  // PATCH en vuelo o uno que falló.
  const hayPendientes = () => pendientesRef.current.size > 0;

  // Vacía la cola con un PATCH por partido.
  //
  // El PATCH escribe SÓLO ese partido, así que dos dispositivos cargando resultados
  // de la misma fecha al mismo tiempo ya no se pisan: antes se mandaba el torneo
  // entero y ganaba el último en guardar. Cada respuesta trae el torneo actualizado,
  // que se mezcla en memoria para ver lo que cargaron los demás.
  const guardarPendientes = async () => {
    if (guardandoRef.current) return; // ya hay una tanda en vuelo; ésta la toma el finally
    const torneoId = torneoRef.current && torneoRef.current._id;
    const tanda = Array.from(pendientesRef.current.entries());
    if (!torneoId || tanda.length === 0) return;

    guardandoRef.current = true;
    setSaveState("saving");
    let fallo = false;
    let ultimoRecord = null;
    try {
      // En serie: son uno o dos partidos por tanda y cada respuesta ya trae el
      // torneo entero, así que la última es la más fresca.
      for (const [partidoId, item] of tanda) {
        const data = await guardarPartido(torneoId, {
          ruedaId: item.ruedaId,
          zonaId: item.zonaId,
          fechaId: item.fechaId,
          partidoId: partidoId,
          ...resultadoAlServidor(item.partido),
        });
        // Si mientras corría el PATCH se volvió a editar ese mismo partido, lo que
        // hay en la cola es más nuevo que lo que se acaba de grabar: se deja.
        if (pendientesRef.current.get(partidoId) === item) pendientesRef.current.delete(partidoId);
        ultimoRecord = data && data.record;
      }

      if (ultimoRecord && ultimoRecord._id && torneoRef.current && torneoRef.current._id === torneoId) {
        setTorneo(
          mezclarTorneo(
            torneoRef.current,
            mapToViewModel(ultimoRecord),
            new Set(pendientesRef.current.keys())
          )
        );
      }
      setSaveState("saved");
    } catch (ex) {
      fallo = true;
      setSaveState("error");
      const estado = ex.response && ex.response.status;
      if (estado === 404) {
        // El partido (o el torneo) ya no está: reintentar no lo va a arreglar y
        // dejarlo en la cola haría fallar todas las tandas siguientes.
        pendientesRef.current.clear();
        toastBigError("No se pudo guardar", "Ese partido ya no existe en el torneo. Recargá la pantalla.");
      } else {
        toastBigError("No se pudo guardar", "Revisá la conexión e intentá de nuevo.");
      }
    } finally {
      guardandoRef.current = false;
      // Lo que se cargó mientras corría la tanda va en la vuelta siguiente. Si algo
      // falló no se reintenta solo (quedaría reintentando cada 700 ms sin conexión):
      // lo levanta el próximo resultado que se cargue, y hasta entonces el aviso al
      // cerrar la pantalla lo mantiene visible.
      if (!fallo && hayPendientes()) setPendientesVersion((v) => v + 1);
    }
  };

  useEffect(() => {
    if (pendientesVersion === 0) return undefined;
    const timer = setTimeout(guardarPendientes, ESPERA_GUARDADO);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendientesVersion]);

  useEffect(() => {
    if (saveState !== "saved") return undefined;
    const t = setTimeout(() => setSaveState("idle"), 1600);
    return () => clearTimeout(t);
  }, [saveState]);

  // Aviso al cerrar/refrescar mientras haya un resultado sin persistir (el debounce
  // corriendo, o un PATCH que falló). Sin esto el resultado se pierde en silencio y
  // en pantalla se veía cargado.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!hayPendientes()) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const selectTorneo = async (id) => {
    await loadTorneoInto(id);
    push({ level: "ruedas", ano: current.ano, torneoId: id });
  };

  const renderScreen = () => {
    switch (current.level) {
      case "torneos":
        return (
          <PickScreen
            title="Elegí el torneo"
            subtitle={"Año " + current.ano}
            options={torneosForYear(current.ano).map((t) => ({ key: t._id, label: t.nombre }))}
            emptyText="No hay torneos para este año."
            onSelect={selectTorneo}
            onBack={years.length > 1 ? goBack : undefined}
          />
        );
      case "ruedas":
        if (!torneo) return null;
        return (
          <PickScreen
            title="Elegí la rueda"
            subtitle={torneo.nombre}
            options={torneo.ruedas.map((r) => ({
              key: r.id,
              label: r.name,
              badges: [{ label: r.zones.length + " zonas", variant: "teams" }],
            }))}
            emptyText="El torneo no tiene ruedas."
            onSelect={(roundId) => push({ ...current, level: "zonas", roundId })}
            onBack={goBack}
          />
        );
      case "zonas":
        if (!round) return null;
        return (
          <PickScreen
            title="Elegí la zona"
            subtitle={round.name}
            options={round.zones.map((z) => ({
              key: z.id,
              label: z.name,
              accent: "zone",
              badges: [{ label: z.matchDates.length + " fechas", variant: "dates" }],
            }))}
            emptyText="La rueda no tiene zonas."
            onSelect={(zoneId) => push({ ...current, level: "fechas", zoneId })}
            onBack={goBack}
          />
        );
      case "fechas": {
        if (!zone) return null;
        const numeros = numerosDeFecha(zone.matchDates);
        const fechasDesc = ordenarFechasDesc(zone.matchDates);
        return (
          <PickScreen
            title="Elegí la fecha"
            subtitle={zone.name}
            options={fechasDesc.map((f) => ({
              key: f.id,
              label: etiquetaFecha(f, numeros[f.id]),
              accent: "date",
              badges: [{ label: f.matches.length + " partidos", variant: "dates" }],
            }))}
            emptyText="La zona no tiene fechas."
            onSelect={(dateId) => push({ ...current, level: "results", dateId })}
            onBack={goBack}
          />
        );
      }
      case "results": {
        if (!zone || !date) return null;
        const numeroFecha = numerosDeFecha(zone.matchDates)[date.id];
        return (
          <ResultsScreen
            zone={zone}
            date={date}
            teams={torneo.equipos}
            headerTitle={etiquetaFecha(date, numeroFecha)}
            torneoNombre={torneo.nombre}
            torneoAno={torneo.ano}
            numeroFecha={numeroFecha}
            onMatchChange={(updated) => updateMatch(current.roundId, current.zoneId, current.dateId, updated)}
            onBack={goBack}
          />
        );
      }
      case "years":
      default:
        return (
          <PickScreen
            title="Elegí el año"
            options={years.map((y) => ({ key: y, label: String(y) }))}
            emptyText="Todavía no hay torneos cargados."
            onSelect={(ano) => push({ level: "torneos", ano })}
          />
        );
    }
  };

  return (
    <>
      <PanelHeader size="sm" />
      <div className="content">
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                {loading ? <p className="text-muted text-center py-5 my-0">Cargando…</p> : renderScreen()}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      {torneo && torneo._id && saveState !== "idle" && (
        <div className={"save-indicator save-indicator--" + saveState}>
          {saveState === "saving" && "Guardando…"}
          {saveState === "saved" && "Guardado"}
          {saveState === "error" && "Error al guardar"}
        </div>
      )}
    </>
  );
}
